import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { buildIssueCategoryMap } from '../../../constants/issueTypes';
import {
  buildIssueReportContext,
  buildIssueReportEnvelopeIdentity,
  ISSUE_REPORT_HITL_BUILD,
} from '../../../utils/issueReportContext';
import {
  buildIssueReportEnvelope,
  configureIssueReportTransport,
  deliverIssueReportEnvelope,
} from '../../../utils/issueReportTransport';
import {
  IssueClassifySurface,
  IssueReportEventData,
  IssueReportRecord,
  IssueReportSource,
  IssueReportState,
} from './issueReport.props';
// The gate is read here rather than re-derived: one definition of the rule, shared with
// the button that greys itself out for it. `issueReportContext` already imports
// selectors into this dependency chain, so this adds no new kind of coupling.
import { selectCanFileIssueReport } from './issueReport.selectors';

/**
 * HITL-1492 Pillar 3, Part A (Capture).
 *
 * Two invariants this slice exists to hold:
 *
 * 1. `context` is captured once on press and never re-read. The task-router teardown
 *    wipes the session state a later classification would need.
 * 2. Records SURVIVE session teardown. Nothing here reacts to resetAI,
 *    resetSelectedRestaurantDetails, or the restaurant/stage/menu-version resets that
 *    sessionBoundary listens for — HITL-1501's unclassified chip and the pre-lobby
 *    panel both need reports to outlive the session. The only reset is the existing
 *    `user/logout` wipe in rootReducer, which is shift-end and correct.
 */

/**
 * Growth bound in place of a session reset. The event is already sent by the time a
 * record would be evicted, so eviction only loses the Undo/amend affordance on an
 * ancient record — never a report.
 */
export const MAX_RETAINED_REPORTS = 50;

/**
 * How long the flag panel stays open — for Undo AND for classifying. ONE deadline,
 * rendered once, so the two can never disagree.
 *
 * 10s rather than 5s because the panel is now the ONLY classification surface: an
 * agent who does not classify here never can. It is still safe to be short —
 * timing out costs a type, never a report.
 */
export const FLAG_WINDOW_MS = 10000;

/**
 * Absolute ceiling on the window, measured from `created_at` and enforced REGARDLESS
 * of pause state.
 *
 * Required, not a nicety: focusing the detail field pauses the countdown, so a
 * focused-and-abandoned field would otherwise hold the panel open forever. This is
 * what guarantees every flag reaches a terminal state.
 */
export const MAX_FLAG_WINDOW_MS = 60000;

/**
 * Cap on `issue_detail`. This is the one field a human types into under time
 * pressure, so it is also the most likely place a customer name or card fragment
 * enters the payload, and an unbounded paste would blow the 48 KB keepalive budget
 * on the pagehide path.
 *
 * 200 as of HITL-1608, down from the 500 it shared with TRANSCRIPT_LINE_CHAR_CAP.
 * That parity was the reason for the number and it no longer holds: a transcript
 * line is machine output of whatever length the customer spoke, while this is now a
 * REQUIRED field an agent fills between cars. 200 is about two sentences — enough to
 * say what went wrong, short enough that the field cannot become a place to paste an
 * order. Nothing truncates retroactively; older reports keep whatever they stored.
 */
export const ISSUE_DETAIL_CHAR_CAP = 200;

/**
 * Floor on `issue_detail`, in TRIMMED characters (HITL-1608).
 *
 * The detail is mandatory before a type can be committed, and a mandatory field with
 * no floor is one an agent satisfies with `.` — so the floor is what makes the rule
 * mean anything. Deliberately LOW: it exists to reject a keystroke of punctuation,
 * not to judge the writing. Three characters is the shortest genuinely useful answer
 * ("86'd", "es", "pos") and anything higher starts rejecting real ones.
 *
 * It cannot stop `aaa`. Nothing client-side can, and a longer floor only buys longer
 * junk; what catches that is reading the reports, not the form.
 */
export const ISSUE_DETAIL_MIN_CHARS = 3;

/** Trim, cap, and normalise blank to null so `''` never reaches the warehouse. */
export const sanitizeIssueDetail = (
  detail: string | null | undefined
): string | null => {
  if (typeof detail !== 'string') return null;
  const trimmed = detail.trim();
  return trimmed ? trimmed.slice(0, ISSUE_DETAIL_CHAR_CAP) : null;
};

/**
 * THE mandatory-detail rule, in one place (HITL-1608) — every classification surface
 * and every test reads it from here rather than re-deriving `length >= 3`.
 *
 * Two things make a detail insufficient, and the second is the subtle one:
 *
 * 1. It is blank or shorter than `ISSUE_DETAIL_MIN_CHARS` once trimmed.
 * 2. It is STILL the machine's autofill. The transcript word-click files a
 *    half-sentence (`burger should be`) as revision 0, and that text is long enough
 *    to clear any floor while containing nothing the agent wrote. Counting it would
 *    make the single most common pre-classified report exempt from the rule — the
 *    exact opposite of the intent.
 *
 * `seededDetail` is the text the surface OPENED with, and it only disqualifies when
 * `isAutofilled` says the machine put it there: a detail the agent typed earlier and
 * is now revisiting from the backlog opens the same way and must not be re-demanded.
 */
export const isIssueDetailSufficient = (
  detail: string | null | undefined,
  { seededDetail = '', isAutofilled = false } = {}
): boolean => {
  const trimmed = typeof detail === 'string' ? detail.trim() : '';
  if (trimmed.length < ISSUE_DETAIL_MIN_CHARS) return false;
  if (isAutofilled && trimmed === seededDetail.trim()) return false;
  return true;
};

export const initialState: IssueReportState = {
  byId: {},
  order: [],
  activeReportId: null,
  activeWindowExpiresAt: null,
  remainingMs: null,
};

const evictOldest = (state: IssueReportState) => {
  while (state.order.length > MAX_RETAINED_REPORTS) {
    const oldest = state.order.shift();
    if (oldest) {
      delete state.byId[oldest];
      if (state.activeReportId === oldest) {
        state.activeReportId = null;
        state.activeWindowExpiresAt = null;
      }
    }
  }
};

/**
 * Build the wire payload for a record at its CURRENT revision.
 *
 * `context` rides along on revision 0 and on any later revision whose revision 0 was
 * never acknowledged. Without that second condition a lost rev 0 plus a landed rev 1
 * would leave a classified report with no session join at all — no session_id, no
 * store_code — which is precisely the failure this pillar exists to eliminate. It
 * costs nothing because the context is already frozen on the record, so it survives
 * the task-router teardown that makes the amend context-free in the first place.
 */
export const buildIssueReportEventData = (
  record: IssueReportRecord
): IssueReportEventData => {
  const includeContext = record.revision === 0 || !record.rev0Delivered;
  return {
    schema_version: '1.0',
    hitl_build: ISSUE_REPORT_HITL_BUILD,
    issue_id: record.issue_id,
    revision: record.revision,
    voided: record.voided,
    source: record.source,
    // Derived here rather than stored on the record: one source of truth
    // (ISSUE_TYPES) and no chance of a record carrying a stale pairing.
    issue_category: record.issue_type
      ? buildIssueCategoryMap()[record.issue_type] ?? null
      : null,
    issue_type: record.issue_type,
    issue_detail: record.issue_detail,
    classified_from: record.classified_from,
    context: includeContext ? record.context : null,
  };
};

const issueReportSlice = createSlice({
  name: 'issueReport',
  initialState,
  reducers: {
    reportCreated: (state, action: PayloadAction<IssueReportRecord>) => {
      const record = action.payload;
      state.byId[record.issue_id] = record;
      state.order.push(record.issue_id);
      state.activeReportId = record.issue_id;
      state.activeWindowExpiresAt =
        Date.parse(record.created_at) + FLAG_WINDOW_MS;
      state.remainingMs = null;
      evictOldest(state);
    },
    reportAmended: (
      state,
      action: PayloadAction<{
        issue_id: string;
        issue_type: string | null;
        issue_detail: string | null;
        classified_from?: IssueClassifySurface | null;
      }>
    ) => {
      const { issue_id, issue_type, issue_detail, classified_from } =
        action.payload;
      const record = state.byId[issue_id];
      if (!record) return;
      record.revision += 1;
      record.issue_type = issue_type;
      record.issue_detail = sanitizeIssueDetail(issue_detail);
      // Whatever the record now carries came from a human (every amend path reads the
      // detail field), so the machine's autofill is gone and the mandatory-detail gate
      // must stop treating this text as a seed to be finished. Without this, an agent
      // who extends the word-click sentence, closes, and later reopens the report from
      // the backlog would be asked to write it a second time.
      record.detail_autofilled = false;
      // Per revision, not accumulated: this describes the amend that produced the
      // revision about to be sent. Defaulted to null rather than left alone, so a
      // detail-only amend from an unknown surface cannot inherit the last one's
      // attribution and claim a classification happened where it did not.
      record.classified_from = classified_from ?? null;
      // …and because it is per revision, the STICKY fact rides beside it. Set only by
      // a surface-attributed amend, i.e. a chip click, and never cleared: once an
      // agent has picked a type, a later detail-only amend must not make the report
      // look unconfirmed again. See `type_confirmed`.
      if (classified_from) record.type_confirmed = true;
      record.status = 'pending';
    },
    reportVoided: (state, action: PayloadAction<{ issue_id: string }>) => {
      const record = state.byId[action.payload.issue_id];
      if (!record) return;
      // Idempotent (HITL-1499): a double-click on the toast's Undo inside one
      // frame would otherwise bump the revision twice and emit TWO retractions,
      // at revisions 1 and 2. One retraction per report, whatever the agent does
      // to the button.
      if (record.voided) return;
      record.revision += 1;
      record.voided = true;
      record.status = 'pending';
    },
    sendSucceeded: (
      state,
      action: PayloadAction<{ issue_id: string; revision: number }>
    ) => {
      const { issue_id, revision } = action.payload;
      const record = state.byId[issue_id];
      if (!record) return;
      if (revision === 0) {
        record.rev0Delivered = true;
      }
      // Only the newest revision governs the record's status; a late ack for an older
      // revision must not mark a freshly amended record as fully sent.
      if (revision >= record.revision) {
        record.status = 'sent';
      }
    },
    sendFailed: (
      state,
      action: PayloadAction<{ issue_id: string; revision: number }>
    ) => {
      const { issue_id, revision } = action.payload;
      const record = state.byId[issue_id];
      if (!record) return;
      // Same staleness guard as sendSucceeded above: a send is in flight per
      // revision, so a failure for an OLDER revision must not mark a record that has
      // since been amended. Without this, a slow rev-0 failure lands on a rev-1
      // record the agent has already re-classified and reports it as failed.
      if (revision < record.revision) return;
      record.status = 'failed';
    },
    closeFlagWindow: (state) => {
      state.activeReportId = null;
      state.activeWindowExpiresAt = null;
      state.remainingMs = null;
    },
    /**
     * Stop the countdown while the agent types a detail.
     *
     * PAUSE, not extend. Extending on each keystroke has no defined exit — an agent
     * pulled into an order mid-sentence would leave a panel that never closes — and a
     * per-keystroke reset is a second clock racing the first.
     *
     * `expiresAt` is an absolute timestamp, so pausing cannot just stall an interval:
     * the remainder is banked here and `expiresAt` is nulled. The two are MUTUALLY
     * EXCLUSIVE — a paused window always has `expiresAt === null`.
     *
     * MAX_FLAG_WINDOW_MS still applies; see useIssueFlagWindow.
     */
    pauseFlagWindow: (state) => {
      if (!state.activeReportId || state.activeWindowExpiresAt === null) return;
      state.remainingMs = Math.max(0, state.activeWindowExpiresAt - Date.now());
      state.activeWindowExpiresAt = null;
    },
    /** Resume from the BANKED remainder, never from a fresh FLAG_WINDOW_MS. */
    resumeFlagWindow: (state) => {
      if (!state.activeReportId || state.remainingMs === null) return;
      state.activeWindowExpiresAt = Date.now() + state.remainingMs;
      state.remainingMs = null;
    },
  },
});

export const {
  reportCreated,
  reportAmended,
  reportVoided,
  sendSucceeded,
  sendFailed,
  closeFlagWindow,
  pauseFlagWindow,
  resumeFlagWindow,
} = issueReportSlice.actions;

/**
 * Wire the transport and hand it the current record so it can be delivered.
 *
 * ONE emission per revision, over REST. There is no websocket mirror: it duplicated
 * every rev-0 report onto a second envelope whose only shared key was
 * `(issue_id, revision)`, and — because the two seq counters both start at 0 and the
 * websocket one advanced on rev 0 only — the first report of a tab shipped TWO
 * `issue_report` events both carrying `seq: 0`. It bought no reliability either:
 * `socketMiddleware` drops silently when the socket is closed, which is the same
 * window the REST send fails in, and REST is the path with retries and a pagehide
 * flush. Envelope `id` is the dedupe key here, as it is for every other event type.
 */
const deliver = createAsyncThunk(
  'issueReport/deliver',
  async ({ issueId }: { issueId: string }, { getState, dispatch }) => {
    const state = getState();
    const record = state.issueReport.byId[issueId];
    if (!record) return;

    configureIssueReportTransport(
      {
        eventAPI: state.config.EVENT_API,
        eventAPIAuth: state.config.EVENT_API_BASIC_AUTH_PARAM,
      },
      {
        onSuccess: (issue_id, revision) =>
          dispatch(sendSucceeded({ issue_id, revision })),
        onFailure: (issue_id, revision) =>
          dispatch(sendFailed({ issue_id, revision })),
      }
    );

    const data = buildIssueReportEventData(record);
    const { email, username, id, firstName, lastName } =
      state.user?.userProfile || ({} as any);

    deliverIssueReportEnvelope(
      buildIssueReportEnvelope({
        data,
        // Frozen at press time, NOT read live: by the time an amend is submitted at
        // the task-router boundary these have been wiped from Redux.
        sessionId: record.envelopeIdentity.session_id,
        restaurantCode: record.envelopeIdentity.restaurant_code,
        laneId: record.envelopeIdentity.lane_id,
        agentMetadata: {
          agent_name: username,
          agent_id: id,
          agent_email: email,
          agent_first_name: firstName,
          agent_last_name: lastName,
        },
      })
    );
  }
);

/**
 * Flag an issue. O(1) synchronous work on the press path — a uuid and one pure
 * context build — and NO network on this call stack (NFR-1).
 *
 * ONE OPEN REPORT AT A TIME. A press while a flag window is open is dropped, and
 * that guard lives HERE rather than on the button on purpose: the button is one of
 * three entry points. `Alt+I` is another and the `transcript_turn` gestures are a
 * third, so a guard in the component is a guard the other two walk straight past.
 * The button is ALSO `disabled` while a window is open, so a mouse press is both
 * ignored and visibly unavailable; the keyboard paths rely on this guard alone.
 *
 * ## The guard stays, but its reason narrowed (HITL-1576)
 *
 * It was a correctness guard. `reportCreated` points the flag window at the NEWEST
 * report, so a second press silently swapped the panel over and the first report lost
 * its ONLY chance to be classified — permanently, because `Unclassified` was terminal.
 * Losing a classification for good, with no indication to the agent, was the cost.
 *
 * HITL-1571 removed that cost: a swapped-out report now goes to the outstanding
 * backlog and is classifiable for the rest of the shift. So the reason is smaller and
 * purely about the surface: **the panel must not change under the agent
 * mid-classification.** They are looking at a 24-chip grid for one specific report,
 * and replacing it with another report's grid — same layout, different subject — is
 * how a chip gets clicked for the wrong thing.
 *
 * Written down because the premise changed and the guard did not. Do not delete this
 * on the grounds that Unclassified is no longer terminal; that was never the whole
 * argument, and it is no longer any of it.
 *
 * KNOWN COST, accepted: a second problem arising inside the same 10s window is not
 * filed at all until the panel closes. The agent has to press again, and nothing
 * reminds them to.
 *
 * Scoped to the WINDOW, not the session: once the panel closes the next press files
 * normally. A session legitimately has several separate problems.
 *
 * ## Why the gesture's type rides on REVISION 0
 *
 * `issue_type` / `issue_detail` ride on revision 0 when the caller already knows
 * them — the transcript gestures do, because clicking a word IS the gesture that
 * classifies. The alternative is filing unclassified and amending immediately after.
 *
 * That alternative used to be unsafe: an agent pulled into the order between the file
 * and the amend lost the type outright, since `Unclassified` was terminal. Amending
 * is now safe, so this is no longer a correctness argument — it is just ONE envelope
 * instead of two for a single gesture, which is reason enough to keep it. Behaviour
 * unchanged; only the justification is weaker.
 *
 * The action-bar press passes neither and still files unclassified.
 *
 * ## NO ACTIVE ORDER, NO NEW REPORT
 *
 * `selectCanFileIssueReport` — `isCarPresent || sessionStatus === 'start'`. Agents were
 * pressing `Issue` on an idle console, and those reports join either the DEPARTED car's
 * session (`endedSessionId` is deliberately in `resolveIssueReportSessionId`'s chain) or
 * the `OUT_OF_SESSION` sentinel, so they describe nothing that happened.
 *
 * Read the predicate as "is there an active order interaction", not "is a car at the
 * board" — it deliberately still allows filing while an order tail is unsettled after
 * `car_exit`. The full reasoning, the three values of `sessionStatus` and the one
 * accepted hole are on the selector; do not restate them here, and do not narrow this to
 * `isCarPresent` without reading them.
 *
 * IT LIVES HERE for the same reason the guard above does, and more strongly: there are
 * FOUR entry points, not three. The button, `Alt+I`, a transcript word click and a click
 * on the empty transcript area (which exists to mean "nothing was transcribed" and is
 * therefore reachable precisely BECAUSE no car is present). All four dispatch this thunk;
 * `reportCreated` is dispatched from one line, below. A guard in any component is a guard
 * the other three walk past.
 *
 * FILING ONLY. `commitIssueType` and `voidIssue` are not gated — the outstanding backlog
 * exists to be worked between cars.
 */
export const flagIssue = createAsyncThunk(
  'issueReport/flagIssue',
  async (
    {
      source = 'action_bar' as IssueReportSource,
      issue_type = null,
      issue_detail = null,
    }: {
      source?: IssueReportSource;
      issue_type?: string | null;
      issue_detail?: string | null;
    },
    { getState, dispatch }
  ) => {
    const state = getState();
    // `activeReportId` IS "a flag window is open" — `closeFlagWindow` and
    // `voidIssue` both null it, and the hard cap guarantees one of them runs.
    if (state.issueReport.activeReportId) return null;
    // NO ACTIVE ORDER, NO NEW REPORT. Second, because the window guard is the
    // narrower and older rule and callers already depend on its `null`.
    if (!selectCanFileIssueReport(state)) return null;

    const issueId = uuidv4();

    const record: IssueReportRecord = {
      issue_id: issueId,
      envelopeIdentity: buildIssueReportEnvelopeIdentity(state),
      revision: 0,
      status: 'pending',
      voided: false,
      rev0Delivered: false,
      source,
      created_at: new Date().toISOString(),
      issue_type,
      issue_detail: sanitizeIssueDetail(issue_detail),
      // True only for a gesture that files WITH a detail, which today means the
      // transcript word click and nothing else. It is what stops that autofill
      // satisfying the mandatory-detail rule — see `isIssueDetailSufficient`.
      detail_autofilled: Boolean(sanitizeIssueDetail(issue_detail)),
      // Always null on revision 0, even for a transcript gesture that files WITH a
      // type. A word click is not a surface the agent classified from — it is the
      // machine's guess riding along with the file, and the agent has yet to confirm
      // it. Attributing it to a surface here would make a guess look like a decision,
      // and it is exactly the distinction this field exists to draw.
      classified_from: null,
      // False even when this file carries a type: an ASR guess is not a confirmation,
      // which is the whole reason a word-click report stays in the backlog.
      type_confirmed: false,
      context: buildIssueReportContext(state),
    };

    dispatch(reportCreated(record));
    dispatch(deliver({ issueId }));
    return issueId;
  }
);

/**
 * Classify a report. One thunk for BOTH classification paths — HITL-1500's tray sets
 * the type alone, HITL-1501's panel also passes `issue_detail`. Two thunks would
 * drift on revision handling.
 */
export const commitIssueType = createAsyncThunk(
  'issueReport/commitIssueType',
  async (
    {
      issue_id,
      issue_type,
      issue_detail = null,
      classified_from = null,
    }: {
      issue_id: string;
      issue_type: string | null;
      issue_detail?: string | null;
      /**
       * The surface this came from. Null for a commit that classifies nothing — the
       * detail-only commit a surface makes on its way out with no type chosen.
       */
      classified_from?: IssueClassifySurface | null;
    },
    { dispatch }
  ) => {
    dispatch(
      reportAmended({ issue_id, issue_type, issue_detail, classified_from })
    );
    // Closing the window is the CALLER's job, not this thunk's: the panel closes on
    // a chip click, but also commits on timeout — where the window is already gone.
    dispatch(deliver({ issueId: issue_id }));
  }
);

/**
 * Undo. Emits a RETRACTION at the next revision — it does not cancel the revision-0
 * send, because committing on press is the whole point: a refresh, a car exit or a
 * network blip inside the flag window would otherwise lose the flag, and blips are
 * exactly what agents are flagging.
 */
export const voidIssue = createAsyncThunk(
  'issueReport/voidIssue',
  async ({ issue_id }: { issue_id: string }, { getState, dispatch }) => {
    // Guarded here as well as in the reducer: the reducer guard keeps the
    // revision at 1, but without this a second click would still SEND revision 1
    // a second time. Consumers dedupe on (issue_id, revision), so that is
    // survivable — it is just a wasted request on the press path.
    const record = getState().issueReport.byId[issue_id];
    if (!record || record.voided) return;

    dispatch(reportVoided({ issue_id }));
    dispatch(closeFlagWindow());
    dispatch(deliver({ issueId: issue_id }));
  }
);

export default issueReportSlice.reducer;
